type BreadcrumbItem = {
  name: string;
  path: string;
};

type RobotsConfig = {
  index: boolean;
  follow: boolean;
};

type SchemaObject = Record<string, unknown>;

export type SeoRoute = {
  path: string;
  title: string;
  description: string;
  canonicalUrl: string;
  imageUrl: string;
  imageAlt: string;
  ogType: "website";
  robots: RobotsConfig;
  robotsContent: string;
  includeInSitemap: boolean;
  priority: number;
  structuredData: SchemaObject[];
};

const siteConfig = {
  siteUrl: "https://beton2go-romano.de",
  siteName: "Beton2Go Romano",
  companyName: "Beton2Go by Romano",
  businessName: "Betontankstelle Giuseppe Romano",
  locale: "de_DE",
  language: "de-DE",
  defaultImagePath: "/og-image.jpg",
  logoPath: "/logo.png",
  contactEmail: "info@beton2go-romano.de",
  contactPhone: "+49 151 41315272",
  address: {
    streetAddress: "Primsaue 5",
    postalCode: "66809",
    addressLocality: "Nalbach",
    addressCountry: "DE",
  },
} as const;

const indexedRobots: RobotsConfig = {
  index: true,
  follow: true,
};

const noindexRobots: RobotsConfig = {
  index: false,
  follow: true,
};

const getCanonicalPath = (path: string) => {
  if (path === "/") {
    return "/";
  }

  return path.endsWith("/") ? path.slice(0, -1) : path;
};

const buildCanonicalUrl = (path: string) => {
  const normalizedPath = getCanonicalPath(path);
  return new URL(normalizedPath, siteConfig.siteUrl).toString();
};

const buildRobotsContent = (robots: RobotsConfig) =>
  [
    robots.index ? "index" : "noindex",
    robots.follow ? "follow" : "nofollow",
    "max-image-preview:large",
    "max-snippet:-1",
    "max-video-preview:-1",
  ].join(",");

const buildBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: buildCanonicalUrl(item.path),
  })),
});

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${siteConfig.siteUrl}/#website`,
  url: `${siteConfig.siteUrl}/`,
  name: siteConfig.siteName,
  alternateName: siteConfig.companyName,
  inLanguage: siteConfig.language,
  publisher: {
    "@id": `${siteConfig.siteUrl}/#organization`,
  },
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${siteConfig.siteUrl}/#organization`,
  name: siteConfig.siteName,
  alternateName: siteConfig.businessName,
  url: `${siteConfig.siteUrl}/`,
  logo: {
    "@type": "ImageObject",
    url: buildCanonicalUrl(siteConfig.logoPath),
  },
  image: buildCanonicalUrl(siteConfig.defaultImagePath),
  email: siteConfig.contactEmail,
  telephone: siteConfig.contactPhone,
  address: {
    "@type": "PostalAddress",
    ...siteConfig.address,
  },
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${siteConfig.siteUrl}/#localbusiness`,
  name: siteConfig.siteName,
  alternateName: siteConfig.businessName,
  url: `${siteConfig.siteUrl}/`,
  image: buildCanonicalUrl(siteConfig.defaultImagePath),
  email: siteConfig.contactEmail,
  telephone: siteConfig.contactPhone,
  address: {
    "@type": "PostalAddress",
    ...siteConfig.address,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "06:00",
      closes: "20:00",
    },
  ],
};

const buildWebPageSchema = (route: {
  canonicalUrl: string;
  title: string;
  description: string;
  pageType?: "WebPage" | "CollectionPage";
}) => ({
  "@context": "https://schema.org",
  "@type": route.pageType ?? "WebPage",
  "@id": `${route.canonicalUrl}#webpage`,
  url: route.canonicalUrl,
  name: route.title,
  description: route.description,
  inLanguage: siteConfig.language,
  isPartOf: {
    "@id": `${siteConfig.siteUrl}/#website`,
  },
});

const buildDocumentCollectionSchema = (canonicalUrl: string) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "@id": `${canonicalUrl}#documents`,
  name: "Betoninfos und Downloads",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      item: {
        "@type": "DigitalDocument",
        name: "Nachbehandlung von Beton",
        description: "Hinweise zur fachgerechten Nachbehandlung von Frischbeton.",
        encodingFormat: "application/pdf",
        url: buildCanonicalUrl("/downloads/beton2go_nachbehandlung.pdf"),
      },
    },
    {
      "@type": "ListItem",
      position: 2,
      item: {
        "@type": "DigitalDocument",
        name: "Wasserzugabe",
        description: "Merkblatt zur richtigen Wasserzugabe bei Frischbeton.",
        encodingFormat: "application/pdf",
        url: buildCanonicalUrl("/downloads/beton2go_wasserzugabe.pdf"),
      },
    },
    {
      "@type": "ListItem",
      position: 3,
      item: {
        "@type": "DigitalDocument",
        name: "Sicherheitsdatenblatt",
        description: "Sicherheitsinformationen für den Umgang mit Frischbeton.",
        encodingFormat: "application/pdf",
        url: buildCanonicalUrl("/downloads/beton2go_sicherheitsdatenblatt.pdf"),
      },
    },
  ],
});

const buildSeoRoute = ({
  path,
  title,
  description,
  robots,
  includeInSitemap,
  priority,
  breadcrumbs,
  pageType,
  extraStructuredData = [],
}: {
  path: string;
  title: string;
  description: string;
  robots: RobotsConfig;
  includeInSitemap: boolean;
  priority: number;
  breadcrumbs?: BreadcrumbItem[];
  pageType?: "WebPage" | "CollectionPage";
  extraStructuredData?: SchemaObject[];
}): SeoRoute => {
  const canonicalUrl = buildCanonicalUrl(path);
  const structuredData: SchemaObject[] = [buildWebPageSchema({ canonicalUrl, title, description, pageType })];

  if (breadcrumbs && breadcrumbs.length >= 2) {
    structuredData.push(buildBreadcrumbSchema(breadcrumbs));
  }

  structuredData.push(...extraStructuredData);

  return {
    path,
    title,
    description,
    canonicalUrl,
    imageUrl: buildCanonicalUrl(siteConfig.defaultImagePath),
    imageAlt: "Beton2Go Romano in Nalbach",
    ogType: "website",
    robots,
    robotsContent: buildRobotsContent(robots),
    includeInSitemap,
    priority,
    structuredData,
  };
};

const routeMap = {
  "/": buildSeoRoute({
    path: "/",
    title: "Betontankstelle in Nalbach | Frischbeton ab 0,15 m³ | Beton2Go Romano",
    description:
      "Beton2Go Romano ist Ihre Betontankstelle in Nalbach: Frischbeton in Selbstbedienung, klare Preise, schnelle Abholung und Öffnungszeiten von Montag bis Samstag.",
    robots: indexedRobots,
    includeInSitemap: true,
    priority: 1,
    extraStructuredData: [websiteSchema, organizationSchema, localBusinessSchema],
  }),
  "/funktion": buildSeoRoute({
    path: "/funktion",
    title: "So funktioniert die Betontankstelle | Beton2Go Romano Nalbach",
    description:
      "Erfahren Sie Schritt für Schritt, wie die Abholung bei Beton2Go Romano in Nalbach funktioniert: Auswahl, Bezahlung, QR-Code, Verladung und wichtige Transporthinweise.",
    robots: indexedRobots,
    includeInSitemap: true,
    priority: 0.8,
    breadcrumbs: [
      { name: "Startseite", path: "/" },
      { name: "Funktion", path: "/funktion" },
    ],
  }),
  "/betoninfos": buildSeoRoute({
    path: "/betoninfos",
    title: "Betoninfos, Sicherheitsdatenblatt und Downloads | Beton2Go Romano",
    description:
      "Alle wichtigen Betoninfos auf einen Blick: Sicherheitsdatenblatt, Hinweise zur Nachbehandlung und Informationen zur Wasserzugabe für Frischbeton von Beton2Go Romano.",
    robots: indexedRobots,
    includeInSitemap: true,
    priority: 0.7,
    breadcrumbs: [
      { name: "Startseite", path: "/" },
      { name: "Betoninfos", path: "/betoninfos" },
    ],
    pageType: "CollectionPage",
    extraStructuredData: [buildDocumentCollectionSchema(buildCanonicalUrl("/betoninfos"))],
  }),
  "/impressum": buildSeoRoute({
    path: "/impressum",
    title: "Impressum | Beton2Go Romano",
    description: "Impressum und Anbieterkennzeichnung der Website von Beton2Go Romano in Nalbach.",
    robots: noindexRobots,
    includeInSitemap: false,
    priority: 0.1,
  }),
  "/datenschutz": buildSeoRoute({
    path: "/datenschutz",
    title: "Datenschutzerklärung | Beton2Go Romano",
    description:
      "Datenschutzerklärung von Beton2Go Romano mit Informationen zu Hosting, Kontaktanfragen, Consent-Management und Google Maps.",
    robots: noindexRobots,
    includeInSitemap: false,
    priority: 0.1,
  }),
  "/cookies": buildSeoRoute({
    path: "/cookies",
    title: "Cookie-Einstellungen | Beton2Go Romano",
    description:
      "Verwalten Sie Ihre Cookie- und Datenschutz-Einstellungen für externe Medien auf der Website von Beton2Go Romano.",
    robots: noindexRobots,
    includeInSitemap: false,
    priority: 0.1,
  }),
} as const;

const notFoundBase = {
  title: "Seite nicht gefunden | Beton2Go Romano",
  description:
    "Die angeforderte Seite wurde nicht gefunden. Nutzen Sie die Startseite von Beton2Go Romano für alle Informationen zur Betontankstelle in Nalbach.",
};

const normalizePath = (pathname: string) => {
  if (!pathname || pathname === "/") {
    return "/";
  }

  const trimmedPath = pathname.split("?")[0].split("#")[0];
  return trimmedPath.endsWith("/") ? trimmedPath.slice(0, -1) : trimmedPath;
};

export const allSeoRoutes = [...Object.values(routeMap), buildSeoRoute({
  path: "/404",
  title: notFoundBase.title,
  description: notFoundBase.description,
  robots: noindexRobots,
  includeInSitemap: false,
  priority: 0.1,
})];

export const getSeoRoute = (pathname: string): SeoRoute => {
  const normalizedPath = normalizePath(pathname);
  const matchedRoute = routeMap[normalizedPath as keyof typeof routeMap];

  if (matchedRoute) {
    return matchedRoute;
  }

  return buildSeoRoute({
    path: normalizedPath,
    title: notFoundBase.title,
    description: notFoundBase.description,
    robots: noindexRobots,
    includeInSitemap: false,
    priority: 0.1,
  });
};

export const seoSiteConfig = siteConfig;
